// Soft warn lets admins intervene before hard block; 80% threshold matches
// budget UX convention. Cap set per workflow or org-wide; FE only displays.
// Live cost updates via SSE cost.tick events; 120ms background-color
// transition (no keyframe needed) provides subtle flash on value change.

import { cn } from "@/utils";
import { DollarSign } from "lucide-react";

export type CostTone = "ok" | "warn" | "danger";

export interface CostBudgetChipProps {
  /** Current spend in USD */
  currentUsd: number;
  /** Budget cap in USD — null when no cap configured */
  capUsd: number | null;
  /** 'workflow' = today's totals · 'run' = current run live cost · 'absolute' = standalone value with explicit thresholds */
  mode: "workflow" | "run" | "absolute";
  /** Explicit tone override — bypasses cap-percentage derivation. Used by 'absolute' mode for per-run row chips where no per-row cap exists. */
  tone?: CostTone;
  className?: string;
}

// Absolute thresholds for 'absolute' mode: ok < $1 · warn $1-$5 · danger > $5.
// Rule of thumb chosen for typical agentic per-run scale; not a regulatory cap.
function deriveAbsoluteTone(value: number): CostTone {
  if (value > 5) return "danger";
  if (value >= 1) return "warn";
  return "ok";
}

function deriveTone(current: number, cap: number | null, mode: "workflow" | "run" | "absolute", explicit?: CostTone): CostTone {
  if (explicit) return explicit;
  if (mode === "absolute") return deriveAbsoluteTone(current);
  if (cap === null || cap <= 0) return "ok";
  const pct = current / cap;
  if (pct >= 1) return "danger";
  if (pct >= 0.8) return "warn";
  return "ok";
}

function formatUsd(value: number): string {
  if (value === 0) return "$0";
  if (value < 0.001) return "<$0.001";
  if (value < 1) return `$${value.toFixed(3)}`;
  return `$${value.toFixed(2)}`;
}

const toneStyles: Record<CostTone, string> = {
  ok: [
    "bg-[color-mix(in_oklch,var(--success)_12%,white)]",
    "border-[color-mix(in_oklch,var(--success)_25%,var(--border))]",
    "text-[oklch(0.30_0.14_150)]",
  ].join(" "),
  warn: [
    "bg-[var(--warning-bg)]",
    "border-[color-mix(in_oklch,var(--warning)_30%,var(--border))]",
    "text-[oklch(0.40_0.12_70)]",
  ].join(" "),
  danger: [
    "bg-[var(--destructive-bg)]",
    "border-[color-mix(in_oklch,var(--destructive)_30%,var(--border))]",
    "text-[var(--destructive)]",
  ].join(" "),
};

/**
 * CostBudgetChip — compact mono pill showing "$cur / $cap".
 * Used in WorkflowHeader (today's totals) and RunDetailPage header (live cost).
 * Background-color transitions at 120ms when `currentUsd` changes from SSE.
 */
export function CostBudgetChip({
  currentUsd,
  capUsd,
  mode,
  tone: explicitTone,
  className,
}: CostBudgetChipProps) {
  const tone = deriveTone(currentUsd, capUsd, mode, explicitTone);
  const label =
    capUsd != null
      ? `${formatUsd(currentUsd)} / ${formatUsd(capUsd)}`
      : formatUsd(currentUsd);

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1",
        "h-[26px] px-2 rounded-full border",
        "font-mono text-[11px] font-semibold select-none",
        "transition-[background-color,border-color,color] duration-[120ms]",
        "[transition-timing-function:cubic-bezier(0.2,0,0,1)]",
        toneStyles[tone],
        className
      )}
      title={capUsd != null ? `${formatUsd(currentUsd)} of ${formatUsd(capUsd)} used` : `Cost: ${formatUsd(currentUsd)}`}
      aria-label={capUsd != null ? `Cost ${formatUsd(currentUsd)} of ${formatUsd(capUsd)}` : `Cost ${formatUsd(currentUsd)}`}
    >
      <DollarSign size={10} strokeWidth={2} aria-hidden className="shrink-0" />
      {label}
    </span>
  );
}

export default CostBudgetChip;
